import React, { useState } from 'react';
import { useRTL } from '../shared/hooks/useRTL';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const Support = () => {
  const { isRTL } = useRTL();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (response || error) {
      setResponse(null);
      setError(null);
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResponse(null);

    if (!formData.name.trim()) {
      setError('اسمك مطلوب');
      setLoading(false);
      return;
    }

    if (!formData.email.trim()) {
      setError('البريد الإلكتروني مطلوب');
      setLoading(false);
      return;
    }

    if (!validateEmail(formData.email)) {
      setError('تنسيق البريد الإلكتروني غير صحيح');
      setLoading(false);
      return;
    }

    if (!formData.message.trim()) {
      setError('يرجى وصف مشكلتك');
      setLoading(false);
      return;
    }

    if (formData.message.trim().length < 10) {
      setError('يرجى كتابة وصف أطول للمشكلة (10 أحرف على الأقل)');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('https://nodjswebhook.onrender.com/api/public/support', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          subject: formData.subject.trim() || 'طلب دعم - تطبيق TAG',
          message: formData.message.trim(),
          timestamp: new Date().toISOString()
        })
      });

      let result;
      try {
        result = await response.json();
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        setError('خطأ في تحليل استجابة الخادم');
        setLoading(false);
        return;
      }

      if (response.ok && result.success) {
        setResponse(result);
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      } else {
        setError(result.error || result.message || `خطأ في الخادم (${response.status})`);
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('خطأ في الاتصال: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen bg-background py-8 ${isRTL ? 'rtl' : 'ltr'}`}>
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <PageHeader
          className="mb-12 flex-col items-center text-center sm:items-center"
          title="اتصال بنا - الدعم الفني"
          description="نحن هنا لمساعدتك! املأ النموذج أدناه وسنقوم بالرد عليك في أقرب وقت ممكن"
        />

        <div className="lg:flex lg:gap-8">
          <aside className="mb-8 space-y-4 lg:mb-0 lg:w-80 lg:flex-shrink-0">
            <Card>
              <CardHeader>
                <div className="text-3xl">📧</div>
                <CardTitle className="text-lg">البريد الإلكتروني</CardTitle>
              </CardHeader>
              <CardContent>
                <a href="mailto:ur.r@hotmail.com" className="break-all text-primary hover:underline">
                  ur.r@hotmail.com
                </a>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="text-3xl">⏰</div>
                <CardTitle className="text-lg">وقت الاستجابة</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">نحاول الرد خلال 24 ساعة</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="text-3xl">🎯</div>
                <CardTitle className="text-lg">كيف نساعدك؟</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2"><span className="text-emerald-500">✓</span><span>استفسارات عامة</span></li>
                  <li className="flex items-start gap-2"><span className="text-emerald-500">✓</span><span>مشاكل تقنية</span></li>
                  <li className="flex items-start gap-2"><span className="text-emerald-500">✓</span><span>مساعدة في الاستخدام</span></li>
                  <li className="flex items-start gap-2"><span className="text-emerald-500">✓</span><span>اقتراحات وتحسينات</span></li>
                </ul>
              </CardContent>
            </Card>
          </aside>

          <div className="lg:flex-1">
            <Card>
              <CardContent className="pt-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">الاسم الكامل <span className="text-destructive">*</span></Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="أدخل اسمك الكامل"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">البريد الإلكتروني <span className="text-destructive">*</span></Label>
                    <Input
                      id="email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="example@email.com"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject">موضوع الرسالة (اختياري)</Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      placeholder="موضوع الرسالة"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">وصف المشكلة أو الاستفسار <span className="text-destructive">*</span></Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={8}
                      placeholder="يرجى وصف مشكلتك أو استفسارك بالتفصيل..."
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>الحد الأدنى: 10 أحرف</span>
                      <span>{formData.message.length} حرف</span>
                    </div>
                  </div>

                  <div className="flex justify-center pt-4">
                    <Button type="submit" size="lg" disabled={loading}>
                      {loading ? 'جاري الإرسال...' : '📤 إرسال الرسالة'}
                    </Button>
                  </div>
                </form>

                {response && (
                  <div className="mt-6 rounded-lg border border-emerald-500/50 bg-emerald-500/10 p-6">
                    <h3 className="mb-2 font-bold text-emerald-700 dark:text-emerald-400">تم إرسال رسالتك بنجاح!</h3>
                    <p className="text-emerald-700 dark:text-emerald-400">
                      {response.message || 'شكراً لتواصلك معنا. سنقوم بالرد عليك في أقرب وقت ممكن.'}
                    </p>
                    {response.ticketId && (
                      <p className="mt-2 font-mono text-sm text-muted-foreground">رقم التذكرة: {response.ticketId}</p>
                    )}
                  </div>
                )}

                {error && (
                  <div className="mt-6 rounded-lg border border-destructive/50 bg-destructive/10 p-6">
                    <h3 className="mb-2 font-bold text-destructive">حدث خطأ</h3>
                    <p className="text-destructive">{error}</p>
                    <Button variant="link" className="mt-2 h-auto p-0" onClick={() => setError(null)}>
                      إغلاق
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="mt-8">
              <CardHeader className="text-center">
                <div className="text-5xl">🔄</div>
                <CardTitle>هل تفضل التواصل المباشر؟</CardTitle>
                <CardDescription>يمكنك التواصل معنا مباشرة عبر البريد الإلكتروني</CardDescription>
              </CardHeader>
              <CardContent className="flex justify-center">
                <Button asChild>
                  <a href="mailto:ur.r@hotmail.com">📧 إرسال بريد إلكتروني</a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        <Button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className={`fixed bottom-8 z-50 size-12 rounded-full ${isRTL ? 'left-8' : 'right-8'}`}
          size="icon"
          aria-label="العودة للأعلى"
        >
          ↑
        </Button>
      </div>
    </div>
  );
};

export default Support;
